import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from '../../api';
import * as logger from '../../logger';
import * as store from '../../reducers/store';
import InputGroupContext from '../../scenes/InputGroupContext';
import Guarantor from '../Guarantor';
import GuarantorVerification from '../GuarantorVerification';
import PhoneInput from '../PhoneInput';
import SocialSecurity from '../SocialSecurity';
import EmergencyContact from './EmergencyContact';
import EmergencyContacts from './EmergencyContacts';
import Employer from './Employer';
import Enum from './Enum';
import Environment from './Environment';
import GrossIncome from './GrossIncome';
import SubstanceUsage from './SubstanceUsage';
import WrittenLanguagesInput from './WrittenLanguagesInput';

import { AlcoholUsage } from '../AlcoholUsage';
import { DatePicker } from '../DatePicker';
import { ExerciseRoutine } from '../ExerciseRoutine';
import { FamilyMedicalHistory } from '../FamilyMedicalHistory';
import { GenderIdentity } from '../GenderIdentity';
import { ScheduleLink } from '../ScheduleLink';
import { SexualHealth } from '../SexualHealth';
import { SexualIdentity } from '../SexualIdentity';
import { SexualOrientation } from '../SexualOrientation';
import { TobaccoUsage } from '../TobaccoUsage';
import { UsState } from '../UsState';
import { Address } from './Address';
import { Boolean } from './Boolean';
import { ButtonLink } from './Button';
import { CareProvider } from './CareProvider';
import { CustomContent } from './CustomContent';
import { Email } from './Email';
import { EmploymentStatus } from './EmploymentStatus';
import { EthnicityInput } from './EthnicityInput';
import { LanguageInput } from './LanguageInput';
import { MaritalStatus } from './MaritalStatus';
import { MultiEnum } from './MultiEnum';
import { MultiString } from './MultiString';
import { Pharmacy } from './Pharmacy';
import { PreferredPronounInput } from './PreferredPronoun';
import { RaceInput } from './RaceInput';
import { RelativeDate } from './RelativeDate';
import { ReligionInput } from './ReligionInput';
import { SexInput } from './SexInput';
import { TextAreaInput } from './TextAreaInput';
import { TextInput } from './TextInput';

import { parseValue } from 'common/dist/utils/types';
import { useBookedSlot } from '../../hooks/schedule/useBookedSlot';
import { getLatestValueOfMatching } from '../../utils/formSet';
import { prettify } from '../../utils/phoneNumber';
import { getProviderNameFull } from '../../utils/provider';
import {
  compileLabelFromFieldOrPatient,
  formatString,
  fullPagePrimitive,
  getOptionsFromArray,
  getOptionsIntersection,
  getPreferredPhoneOptions,
} from '../utils';

import defaultStyles from '../../styles/formSetStyles.module.css';
import styles from './index.module.css';
import { NcpdpPharmacy } from './NcpdpPharmacy';

const { Primitive, STATIC_PRIMITIVES } = api.PrevisitFormTemplateFieldConfig;

interface Props {
  practicePreference: api.Previsit.PracticePreference;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  useAlternateEnum: boolean;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => Promise<void> | void;
  navigateToNextPage: () => Promise<void>;
  fields: api.PrevisitFormField.PrevisitFormFieldWithConfig[];
  patient: api.Patient.Patient | null;
  selectedFormSetId: string;
  formSets: store.FormSets;
  handleLastPage?: (formSetId: string, formFieldId: string) => Promise<void>;
  compiledLabel?: string;
  disabled: boolean;
}

export enum StaticStatus {
  Viewed = 'VIEWED',
  Submitted = 'SUBMITTED',
  LastPage = 'LAST_PAGE',
}

function castToBoolean(value: string): boolean | undefined {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  }
  return;
}

function dateToString(date: Date | undefined) {
  if (!date || isNaN(date.valueOf())) {
    return '';
  } else {
    const pad0 = (value: number) => `0${value}`.slice(-2);
    return `${pad0(date.getUTCMonth() + 1)}/${pad0(
      date.getUTCDate(),
    )}/${date.getUTCFullYear()}`;
  }
}

function QuestionRenderer(props: Props) {
  const {
    practicePreference,
    field: {
      id,
      primitive,
      value,
      options,
      min,
      max,
      interval,
      placeholder,
      suffix,
      label: fieldLabel,
      localizedOptions,
      required,
      helpDialog,
      helpDialogLabel,
      lastPage,
      previsitFormSetId,
      sublabel,
      characterLimit,
    },
    useAlternateEnum,
    updateFieldValue,
    saveFieldValue,
    navigateToNextPage,
    fields,
    patient,
    selectedFormSetId,
    handleLastPage,
    compiledLabel,
    disabled,
    formSets,
  } = props;
  const label = compiledLabel || fieldLabel;
  const { t } = useTranslation();
  const [isAlternateSubmitting, setIsAlternateSubmitting] = useState(false);

  if (
    !value &&
    STATIC_PRIMITIVES.includes(primitive) &&
    lastPage &&
    handleLastPage
  ) {
    handleLastPage(previsitFormSetId, id);
  } else if (!value && STATIC_PRIMITIVES.includes(primitive)) {
    updateFieldValue(StaticStatus.Viewed);
    saveFieldValue(StaticStatus.Viewed);
  }

  const saveFieldValueWithAlternate = async (option: string) => {
    updateFieldValue(option);
    if (useAlternateEnum && !isAlternateSubmitting) {
      setIsAlternateSubmitting(true);
      try {
        await saveFieldValue(option);
        setIsAlternateSubmitting(false);
        navigateToNextPage();
      } catch (err) {
        setIsAlternateSubmitting(false);
        logger.error(err);
      }
    } else {
      await saveFieldValue(option);
    }
  };

  switch (primitive) {
    case Primitive.Address:
    case Primitive.PatientAddressMailing:
    case Primitive.PatientAddressPhysical:
      return (
        <Address
          id={`${id}-question`}
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          previsitAddressValidCharacterSet={
            practicePreference.previsitAddressValidCharacterSet
          }
        />
      );
    case Primitive.PatientAlcoholUsage:
      return (
        <AlcoholUsage
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          statuses={practicePreference.alcoholStatuses}
          frequencies={practicePreference.patientAlcoholUsageFrequencies}
          quantities={practicePreference.patientAlcoholUsageQuantities}
        />
      );
    case Primitive.PatientAlcoholUsageConcern:
    case Primitive.PatientSubstanceUsageConcern:
      let valueToOption = value;
      if (valueToOption.length && valueToOption === 'false') {
        valueToOption = 'No';
      } else if (valueToOption.length) {
        valueToOption = 'Yes';
      }
      const conernOptions = getOptionsFromArray(['Yes', 'No'], t);
      return (
        <Enum
          value={valueToOption}
          options={conernOptions}
          onChange={(option) => {
            saveFieldValue(option === 'Yes' ? 'true' : 'false');
            updateFieldValue(option === 'Yes' ? 'true' : 'false');
          }}
        />
      );
    case Primitive.String:
      return (
        <TextAreaInput
          id={`${id}-question`}
          className={defaultStyles.Textarea}
          placeholder={placeholder || undefined}
          value={value || ''}
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
          useDefaultMaxLength={true}
          characterLimit={characterLimit}
        />
      );
    case Primitive.TextInput:
      return (
        <TextInput
          id={`${id}-question`}
          placeholder={placeholder || undefined}
          value={value || ''}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          characterLimit={characterLimit}
        />
      );
    case Primitive.PatientFirstName:
    case Primitive.PatientLastName:
    case Primitive.PatientPreferredFirstName:
      return (
        <TextInput
          id={`${id}-question`}
          placeholder={placeholder || undefined}
          value={value || ''}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.Integer:
      return (
        <div className={defaultStyles.InputWrapper}>
          <input
            id={`${id}-question`}
            className={defaultStyles.Input}
            type="number"
            min={min || undefined}
            max={max || undefined}
            step={interval || undefined}
            value={value}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              updateFieldValue(event.currentTarget.value);
              saveFieldValue(event.currentTarget.value);
            }}
          />
          <span className={styles.Suffix}>{suffix}</span>
        </div>
      );
    case Primitive.Date:
    case Primitive.PatientDob:
    case Primitive.PatientRetirementDate:
      return (
        <DatePicker
          curvedInput={5}
          date={value}
          onChange={(date) => {
            updateFieldValue(dateToString(date));
          }}
        />
      );
    case Primitive.RelativeDate:
      return (
        <RelativeDate
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          value={value}
        />
      );
    case Primitive.ButtonLink:
      const url = sublabel ? sublabel : '';
      const buttonTitle = label ? label : '';

      return (
        <ButtonLink
          buttonTitle={buttonTitle}
          url={url}
          encounterId={formSets[selectedFormSetId].encounter?.id}
        />
      );
    case Primitive.Boolean:
    case Primitive.PatientAddressMailingSameAsPhysical:
    case Primitive.PatientPhoneSameAsCell:
      return (
        <Boolean
          checked={castToBoolean(value)}
          label={label}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.Email:
    case Primitive.PatientEmail:
      return (
        <Email
          label={label}
          id={`${id}-question`}
          placeholder={placeholder || undefined}
          value={value || ''}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientCareProviders:
      return (
        <CareProvider
          id={`${id}-question`}
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientEnvironment:
      return (
        <Environment
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          fieldOptions={{
            livesWithPreferenceOptions:
              practicePreference.patientEnvironmentLivesWithOptions,
            livingSituationPreferenceOptions:
              practicePreference.patientEnvironmentLivingSituationOptions,
            parentsMaritalStatusPreferenceOptions:
              practicePreference.patientEnvironmentParentsMaritalStatusOptions,
            risksPreferenceOptions:
              practicePreference.patientEnvironmentRisksOptions,
          }}
        />
      );
    case Primitive.PatientEnvironmentPediatric:
      return (
        <Environment
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          fieldOptions={{
            livesWithPreferenceOptions:
              practicePreference.patientEnvironmentLivesWithOptions,
            livingSituationPreferenceOptions:
              practicePreference.patientEnvironmentLivingSituationOptions,
            parentsMaritalStatusPreferenceOptions:
              practicePreference.patientEnvironmentParentsMaritalStatusOptions,
            risksPreferenceOptions:
              practicePreference.patientEnvironmentRisksOptions,
          }}
          isPediatric={true}
        />
      );
    case Primitive.PatientEmergencyContacts:
      const parsedEmergencyContactsValue: api.Previsit.PatientEmergencyContact[] =
        value ? JSON.parse(value) : [];

      return (
        <EmergencyContacts
          value={parsedEmergencyContactsValue}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          emergencyContactRelationships={
            practicePreference.emergencyContactRelationships
          }
        />
      );
    case Primitive.PatientEmergencyContact:
    case Primitive.PatientRelativeEmergencyContact:
      return (
        <EmergencyContact
          primitive={primitive}
          emergencyContactRelationships={
            practicePreference.emergencyContactRelationships
          }
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          value={value}
        />
      );
    case Primitive.PatientEmployer:
      return (
        <Employer
          value={value}
          collectEmployerPhoneNumber={
            practicePreference.collectEmployerPhoneNumber
          }
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
        />
      );
    case Primitive.PatientEmploymentStatus:
      return (
        <EmploymentStatus
          value={value}
          preferredEmploymentStatuses={
            practicePreference.employmentStatuses || []
          }
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
          useAlternateEnum={useAlternateEnum}
          navigateToNextPage={navigateToNextPage}
        />
      );
    case Primitive.PatientEthnicity:
      return (
        <EthnicityInput
          value={value}
          preferredEthnicityOptions={
            practicePreference.previsitEthnicityOptions || []
          }
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
          useAlternateEnum={useAlternateEnum}
          navigateToNextPage={navigateToNextPage}
        />
      );
    case Primitive.PatientSex:
      return (
        <SexInput
          value={value}
          preferredSexOptions={practicePreference.previsitSexOptions || []}
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
          useAlternateEnum={useAlternateEnum}
          navigateToNextPage={navigateToNextPage}
        />
      );
    case Primitive.Enum:
      return (
        <Enum
          value={value}
          options={options || undefined}
          localizedOptions={localizedOptions}
          onChange={saveFieldValueWithAlternate}
          useAlternateEnum={useAlternateEnum}
          helpDialog={helpDialog || undefined}
          helpDialogLabel={helpDialogLabel || undefined}
          disabled={disabled || isAlternateSubmitting}
        />
      );
    case Primitive.PatientExerciseRoutine:
      return (
        <ExerciseRoutine
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientGrossIncome:
      const parsedGrossIncomeValue =
        parseValue<
          Partial<api.PrevisitPatientGrossIncome.GrossIncomeFormFieldValue>
        >(value) ?? {};
      return (
        <GrossIncome
          value={parsedGrossIncomeValue}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientGuarantorVerification:
      const parsedGuarantorVerificationValue: api.Guarantor.PrevisitGuarantorVerificationFormFieldValue =
        value ? JSON.parse(value) : {};
      return (
        <GuarantorVerification
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          value={parsedGuarantorVerificationValue}
        />
      );
    case Primitive.PatientGuarantors:
      const parsedGuarantorValue: api.Guarantor.PrevisitGuarantorFormFieldValue =
        value ? JSON.parse(value) : {};
      return (
        <Guarantor
          value={parsedGuarantorValue}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          helpDialog={helpDialog}
        />
      );
    case Primitive.PatientNCPDPPharmacies:
      return (
        <NcpdpPharmacy
          practice={formSets[selectedFormSetId].practice}
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientPharmacies:
      return (
        <Pharmacy
          practice={formSets[selectedFormSetId].practice}
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientRace:
      return (
        <RaceInput
          raceOptions={practicePreference.raceOptions}
          useAlternateEnum={useAlternateEnum}
          value={value}
          navigateToNextPage={navigateToNextPage}
          multi={practicePreference.previsitMultipleRaces}
          onChange={saveFieldValueWithAlternate}
        />
      );
    case Primitive.PatientReligion:
      return (
        <ReligionInput
          religionOptions={practicePreference.religionOptions}
          required={required}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          value={value}
        />
      );
    case Primitive.MultiEnum:
    case Primitive.PatientProblems:
      const parsedMultiEnumValue: string[] = parseValue(value) ?? [];
      return (
        <MultiEnum
          values={parsedMultiEnumValue}
          options={options ? getOptionsFromArray(options, t) : []}
          localizedOptions={localizedOptions}
          onChange={async (stringValues) => {
            updateFieldValue(stringValues);
            await saveFieldValue(stringValues);
          }}
        />
      );
    case Primitive.PatientMaritalStatus:
      return (
        <MaritalStatus
          preferredMaritalStatus={
            practicePreference.previsitMaritalStatusOptions
          }
          navigateToNextPage={navigateToNextPage}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          useAlternateEnum={useAlternateEnum}
          value={value}
        />
      );
    case Primitive.PatientMedicalHistoryFamily:
      const familyConditionLists = _.partition(
        _.sortBy(practicePreference.medicalConditionsOptions, 'name'),
        { defaultList: true },
      );
      return (
        <FamilyMedicalHistory
          value={value}
          options={getOptionsFromArray(
            familyConditionLists[0].map((cond) => cond.name).sort(),
            t,
          )}
          otherOptions={getOptionsFromArray(
            familyConditionLists[1].map((cond) => cond.name).sort(),
            t,
          )}
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
          modalLabel="Which family members have had"
          modalSelectOptions={getOptionsIntersection(
            api.Previsit.FamilyRelationship,
            practicePreference.familyMedicalHistoryRelationships,
          ).map((rel) => ({
            display: t(api.Previsit.familyRelationshipsMap[rel]),
            key: rel,
            value: rel,
          }))}
        />
      );
    case Primitive.PatientMedicalHistoryPersonal:
      const personalConditionLists = _.partition(
        _.sortBy(practicePreference.medicalConditionsOptions, 'name'),
        { defaultList: true },
      );
      return (
        <MultiEnum
          values={(value && JSON.parse(value)) || []}
          options={getOptionsFromArray(
            personalConditionLists[0].map((cond) => cond.name).sort(),
            t,
          )}
          otherOptions={getOptionsFromArray(
            personalConditionLists[1].map((cond) => cond.name).sort(),
            t,
          )}
          sortOptions={true}
          onChange={async (stringValues) => {
            updateFieldValue(stringValues);
            await saveFieldValue(stringValues);
          }}
        />
      );
    case Primitive.MultiString:
      return (
        <MultiString
          value={value}
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
        />
      );
    case Primitive.PatientSubstanceUsage:
      return (
        <SubstanceUsage
          substanceNameOptions={practicePreference.substanceNameOptions}
          substanceUsageStatuses={practicePreference.substanceUsageStatuses}
          substanceUsageFrequencies={
            practicePreference.patientSubstanceUsageFrequencies
          }
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          value={value}
        />
      );

    case Primitive.Phone:
    case Primitive.PatientCellPhone:
    case Primitive.PatientHomePhone:
    case Primitive.PatientPhone:
    case Primitive.PatientWorkPhone:
      return (
        <PhoneInput
          label={label}
          id={`${id}-question`}
          value={value}
          updateFieldValue={updateFieldValue}
          saveFieldValue={saveFieldValue}
        />
      );
    case Primitive.PatientPreferredPhone:
      const phoneOptions = getPreferredPhoneOptions(fields, patient);
      return (
        <Enum
          value={value}
          options={phoneOptions.map((phoneOption) => ({
            display: `${prettify(phoneOption.phoneNumber)} (${t(
              phoneOption.kind + 'PHONE',
            )})`,
            key: phoneOption.kind,
            value: phoneOption.kind,
          }))}
          onChange={saveFieldValueWithAlternate}
          useAlternateEnum={useAlternateEnum}
        />
      );
    case Primitive.PatientPreferredPronoun:
      return (
        <PreferredPronounInput
          pronounOptions={practicePreference.previsitPreferredPronounOptions}
          useAlternateEnum={useAlternateEnum}
          value={value}
          onChange={saveFieldValueWithAlternate}
        />
      );
    case Primitive.PatientPreferredLanguage:
      return (
        <LanguageInput
          value={value}
          languageOptions={practicePreference.previsitPreferredLanguageOptions}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          useAlternateEnum={useAlternateEnum}
          navigateToNextPage={navigateToNextPage}
        />
      );
    case Primitive.PatientSexualHealth:
      return (
        <SexualHealth
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          contraceptives={practicePreference.contraceptiveOptions}
        />
      );

    case Primitive.PatientSexualIdentity:
      return (
        <SexualIdentity
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          required={required}
        />
      );
    case Primitive.PatientSexualOrientation:
      const parsedPatientSexualOrientationValue: api.PatientSocialHistory.SexualOrientationFieldValue =
        value ? JSON.parse(value) : {};
      return (
        <SexualOrientation
          value={parsedPatientSexualOrientationValue}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientGenderIdentity:
      const parsedPatientGenderIdentityValue: api.PatientSocialHistory.GenderIdentityFieldValue =
        value ? JSON.parse(value) : {};
      return (
        <GenderIdentity
          value={parsedPatientGenderIdentityValue}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    case Primitive.PatientSsn:
    case Primitive.PatientSsn4:
      return (
        <SocialSecurity
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          formSetId={selectedFormSetId}
          last4={primitive === Primitive.PatientSsn4}
        />
      );
    case Primitive.PatientTobaccoUsage:
      return (
        <TobaccoUsage
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
          statuses={practicePreference.tobaccoStatuses}
          treatments={practicePreference.tobaccoTreatments}
        />
      );
    case Primitive.PatientWrittenLanguages:
      return (
        <WrittenLanguagesInput
          value={value}
          previsitWrittenLanguageOptions={
            practicePreference.previsitWrittenLanguageOptions
          }
          useAlternateEnum={useAlternateEnum}
          onChange={saveFieldValueWithAlternate}
          multi={practicePreference.previsitMultipleLanguages}
        />
      );
    case Primitive.ScheduleLink:
      return <ScheduleLink />;
    case Primitive.UsState:
      return (
        <UsState
          value={value}
          saveFieldValue={saveFieldValue}
          updateFieldValue={updateFieldValue}
        />
      );
    // rendered specially as one page questions
    case Primitive.AppointmentConfirmation:
    case Primitive.BodyDiagram:
    case Primitive.DocumentSigner:
    case Primitive.FreehandBodyDiagram:
    case Primitive.PatientAllergies:
    case Primitive.PatientBillingDetails:
    case Primitive.PatientCareProvidersPicker:
    case Primitive.PatientInsurance:
    case Primitive.PatientInsuranceCardPhotoUpload:
    case Primitive.PatientInsuranceCardUpload:
    case Primitive.PatientPhotoId:
    case Primitive.PatientPhotoIdUpload:
    case Primitive.PatientMedications:
    case Primitive.Registration:
    case Primitive.ScheduleAppointment:
    case Primitive.ScheduleSeriesAppointment:
    case Primitive.PatientSurgicalHistory:
    case Primitive.VirtualVisitInstructions:
      return null;

    // To be implemented
    case Primitive.Static:
    case Primitive.Float:
      return null;
    case Primitive.CustomContent:
      return <CustomContent content={label} />;

    default:
      const p: never = primitive;
      void p;
      return null;
  }
}

function shouldExtendMargin(
  primitive: api.PrevisitFormTemplateFieldConfig.Primitive,
): boolean {
  return [
    Primitive.Enum,
    Primitive.MultiEnum,
    Primitive.PatientEthnicity,
    Primitive.PatientRace,
  ].includes(primitive);
}

const UNIVERSAL_PRIMITIVES: api.PrevisitFormTemplateFieldConfig.Primitive[] =
  Object.values(Primitive).filter(
    (p) =>
      p !== Primitive.PatientBillingDetails &&
      p.toString().startsWith('PATIENT'),
  );

export function Question(props: Props) {
  const { field, saveFieldValue, updateFieldValue, formSets, fields, patient } =
    props;
  const { selectedEhrProviderWithPractice } = useBookedSlot(field.page);
  const extraParameters: Record<string, string | null> | undefined =
    selectedEhrProviderWithPractice
      ? {
          providerFullName:
            getProviderNameFull({
              provider: selectedEhrProviderWithPractice,
            }) || null,
          providerFirstName: selectedEhrProviderWithPractice.firstName,
          providerLastName: selectedEhrProviderWithPractice.lastName,
          providerPhoneNumber: selectedEhrProviderWithPractice.phoneNumber,
          practicePhoneNumber:
            selectedEhrProviderWithPractice.practicePhoneNumber,
          practiceName: selectedEhrProviderWithPractice.name,
        }
      : undefined;
  const label = compileLabelFromFieldOrPatient({
    label: field.label,
    fields,
    patient,
    page: field.page,
    rank: field.rank,
    extraParameters,
  });
  const {
    Boolean,
    PatientAddressMailingSameAsPhysical,
    PatientEmployer,
    PatientPhoneSameAsCell,
    PatientGuarantors,
    CustomContent,
    ButtonLink,
  } = Primitive;

  // for triggering check once per field
  const lastUpdatedFieldId = useRef<string>();

  useEffect(() => {
    if (!UNIVERSAL_PRIMITIVES.includes(field.primitive)) return;
    if (lastUpdatedFieldId.current === field.id) return;
    lastUpdatedFieldId.current = field.id;
    const latestValue = getLatestValueOfMatching(
      formSets,
      (f) => f.primitive === field.primitive,
    );
    if (latestValue === field.value) return;
    saveFieldValue(latestValue);
    updateFieldValue(latestValue);
  }, [field, saveFieldValue, updateFieldValue, formSets]);

  return (
    <div
      className={[
        'question',
        shouldExtendMargin(field.primitive) ? styles.BiggerMargin : '',
        fullPagePrimitive(field.primitive)
          ? styles.FullPageQuestion
          : styles.Question,
      ].join(' ')}
    >
      {![
        Boolean,
        ButtonLink,
        PatientAddressMailingSameAsPhysical,
        PatientPhoneSameAsCell,
        PatientEmployer,
        PatientGuarantors,
        CustomContent,
      ].includes(field.primitive) && (
        <label
          id={`${field.id}-label`}
          htmlFor={`${field.id}-question`}
          className={styles.Label}
        >
          {formatString(label)}
        </label>
      )}
      <div className={styles.Answer}>
        <InputGroupContext.Provider value={`${field.id}-label`}>
          <QuestionRenderer {...props} compiledLabel={label} />
        </InputGroupContext.Provider>
      </div>
    </div>
  );
}
